import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useEffect, useRef, useState } from 'react';
import { useSetAtom } from 'jotai';
import { appRoles } from '../authConfig';
import { profileDataAtom, type IProfileData } from '../atoms/authAtoms';
import { tenantAtom } from '../atoms/tenantAtom';
import { getFmsAuthorization } from './fmsToken';
import { getProfileData } from '../services/graph';
import { getTenant } from '../services/tenantService';
import { signOut } from '../services/authHelper';

export type AccessStatus =
  | 'checkingAccess'
  | 'signedOut'
  | 'noAccess'
  | 'bootstrapping'
  | 'granted';

interface RoleClaims {
  roles?: string[];
}

export const useAccessStatus = (): AccessStatus => {
  const { instance, inProgress, accounts } = useMsal();
  const setProfileData = useSetAtom(profileDataAtom);
  const setCurrTenant = useSetAtom(tenantAtom);

  const [bootstrapped, setBootstrapped] = useState(false);
  const bootstrapForAccountRef = useRef<string | null>(null);

  let identityStatus: AccessStatus;
  if (inProgress !== InteractionStatus.None) {
    identityStatus = 'checkingAccess';
  } else if (accounts.length === 0) {
    identityStatus = 'signedOut';
  } else {
    const account = instance.getActiveAccount() ?? accounts[0];
    const roles = (account?.idTokenClaims as RoleClaims | undefined)?.roles;
    const hasPortalRole = roles?.some(
      (role) => role === appRoles.Admin || role === appRoles.Viewer
    );
    identityStatus = hasPortalRole ? 'bootstrapping' : 'noAccess';
  }

  useEffect(() => {
    if (identityStatus !== 'bootstrapping') {
      if (identityStatus === 'signedOut' || identityStatus === 'noAccess') {
        setBootstrapped(false);
        bootstrapForAccountRef.current = null;
      }
      return;
    }

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) return;
    if (bootstrapForAccountRef.current === account.homeAccountId) return;
    bootstrapForAccountRef.current = account.homeAccountId;

    let cancelled = false;
    const abortController = new AbortController();
    (async () => {
      try {
        const [profile, authorizeResponse] = await Promise.all([
          getProfileData(instance, accounts),
          getFmsAuthorization(account),
        ]);
        if (cancelled) return;

        const merged: IProfileData = {
          ...profile,
          userPrincipalName: account.username,
          permissions: authorizeResponse.permissions ?? [],
          tenants: authorizeResponse.tenants ?? [],
          currentTenant: authorizeResponse.currentTenant ?? 0,
        };
        setProfileData(merged);

        if (merged.currentTenant) {
          const tenant = await getTenant(abortController, merged.currentTenant);
          if (!cancelled) setCurrTenant(tenant);
        }
        if (!cancelled) setBootstrapped(true);
      } catch (err) {
        if (cancelled) return;
        console.error('FMS bootstrap failed:', err);
        signOut(instance, 'authFailed');
      }
    })();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [identityStatus, accounts, instance, setProfileData, setCurrTenant]);

  if (identityStatus === 'bootstrapping' && bootstrapped) return 'granted';
  return identityStatus;
};
